class KlaviyoSubscribeForm extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('[data-klaviyo-email], [data-newsletter-email]');
    this.primaryInput = this.emailInput;
    this.firstNameInput = this.querySelector('[data-klaviyo-first-name], [data-newsletter-first-name]');
    this.submitButton = this.querySelector('[data-klaviyo-submit], [data-newsletter-submit]');
    this.error = this.querySelector('[data-klaviyo-error], [data-newsletter-error]');
    this.errorText = this.querySelector('[data-klaviyo-error-text], [data-newsletter-error-text]');
    this.success = this.querySelector('[data-klaviyo-success], [data-newsletter-success]');
    this.successText = this.querySelector('[data-klaviyo-success-text], [data-newsletter-success-text]');

    if (!this.form || !this.primaryInput || !this.submitButton) return;

    this.submitButton.disabled = false;
    this.submitButton.setAttribute('aria-busy', 'false');

    const { signal } = this.abortController;
    this.form.addEventListener('submit', this.handleSubmit, { signal });
    this.emailInput?.addEventListener('blur', this.handleEmailBlur, { signal });
    this.primaryInput.addEventListener('input', this.clearFieldError, { signal });
    this.firstNameInput?.addEventListener('input', this.clearFieldError, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();

    // AP: an in-flight submit skips setLoading(false) once its signal is aborted,
    // so a form removed mid-request kept the button disabled with data-adding set.
    // connectedCallback re-enables the button on reinsertion but knows nothing about
    // the attribute (base.css keys the loading spinner on it); clear the whole
    // loading state here so the teardown mirrors the setup.
    this.setLoading(false);
  }

  handleEmailBlur = () => {
    if (!this.emailInput?.value || this.emailInput.validity.valid) return;

    this.showFieldError(this.emailInput, this.dataset.invalidEmailMessage);
  };

  clearFieldError = () => {
    this.primaryInput?.removeAttribute('aria-invalid');
    this.primaryInput?.removeAttribute('aria-describedby');
    this.firstNameInput?.removeAttribute('aria-invalid');
    this.firstNameInput?.removeAttribute('aria-describedby');
    this.hideMessage(this.error);
  };

  validate() {
    if (!this.form.checkValidity()) {
      const invalidInput = this.form.querySelector(':invalid');

      if (invalidInput === this.emailInput) {
        this.showFieldError(this.emailInput, this.dataset.invalidEmailMessage);
      } else if (invalidInput === this.firstNameInput) {
        this.showFieldError(this.firstNameInput, this.dataset.firstNameRequiredMessage);
      } else {
        this.showError(this.dataset.errorMessage);
      }

      invalidInput?.focus();
      return false;
    }

    return true;
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (!this.form || !this.primaryInput || !this.abortController || this.submitButton.disabled) return;

    this.hideMessage(this.success);
    this.hideMessage(this.error);
    if (!this.validate()) return;

    this.clearFieldError();

    if (!this.dataset.companyId || !this.dataset.listId) {
      this.showError(this.dataset.configErrorMessage);
      return;
    }

    this.setLoading(true);
    const { signal } = this.abortController;
    const requestController = new AbortController();
    const cancelRequest = () => requestController.abort();
    signal.addEventListener('abort', cancelRequest, { once: true });
    const timeout = this.requestTimeout ? setTimeout(cancelRequest, this.requestTimeout) : undefined;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.companyId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: this.dataset.revision || '2026-07-15',
          },
          body: JSON.stringify(this.payload),
          signal: requestController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(await this.getErrorMessage(response));
      }

      if (!signal.aborted) this.onSubmitted();
    } catch (error) {
      if (signal.aborted) return;

      this.showRequestError(error);
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', cancelRequest);
      if (!signal.aborted) {
        this.setLoading(false);
      }
    }
  };

  onSubmitted() {
    this.form.reset();
    this.showSuccess(this.dataset.successMessage);
  }

  showRequestError(error) {
    this.showError(error instanceof Error ? error.message : this.dataset.errorMessage);
  }

  get profileAttributes() {
    const profileAttributes = {
      email: this.emailInput.value.trim(),
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };
    const firstName = this.firstNameInput?.value.trim();
    const interests = this.interests;

    if (firstName) {
      profileAttributes.first_name = firstName;
    }

    if (interests) {
      profileAttributes.properties = { Interests: interests };
    }

    return profileAttributes;
  }

  get payload() {
    const subscriptionAttributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: this.profileAttributes,
        },
      },
    };

    if (this.dataset.source) {
      subscriptionAttributes.custom_source = this.dataset.source;
    }

    return {
      data: {
        type: 'subscription',
        attributes: subscriptionAttributes,
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.dataset.listId,
            },
          },
        },
      },
    };
  }

  get interests() {
    const interestInput = this.querySelector('input[name="interests"]');

    if (!interestInput) {
      // No radios on the page, but a drawer may still declare a fixed value:
      // the bride newsletter drawer feeds the same Klaviyo list as the main one,
      // so `Interests` plus `custom_source` are the only signals Klaviyo has to
      // tell its subscribers apart.
      const fixedInterests = this.dataset.interests
        ?.split(',')
        .map((interest) => interest.trim())
        .filter(Boolean);

      return fixedInterests?.length ? fixedInterests : null;
    }

    const selectedInterest = this.querySelector('input[name="interests"]:checked')?.value || 'both';

    if (selectedInterest === 'ready_to_wear') {
      return ['Ready to Wear'];
    }

    if (selectedInterest === 'bride') {
      return ['Bride'];
    }

    return ['Ready to Wear', 'Bride'];
  }

  async getErrorMessage(response) {
    try {
      const body = await response.json();
      const detail = body?.errors?.[0]?.detail;

      if (detail) return detail;
    } catch (_error) {
      return this.dataset.errorMessage;
    }

    return this.dataset.errorMessage;
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;

    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', String(isLoading));

    if (isLoading) {
      this.submitButton.dataset.adding = 'true';
    } else {
      delete this.submitButton.dataset.adding;
    }
  }

  showFieldError(input, message) {
    this.showError(message);
    input?.setAttribute('aria-invalid', 'true');

    if (this.error?.id) {
      input?.setAttribute('aria-describedby', this.error.id);
    }
  }

  showError(message) {
    if (!this.error || !this.errorText) return;

    this.errorText.textContent = message || this.dataset.errorMessage || '';
    this.error.hidden = false;
  }

  showSuccess(message) {
    if (!this.success || !this.successText) return;

    this.successText.textContent = message || '';
    this.success.hidden = false;
    this.success.focus?.();
  }

  hideMessage(message) {
    if (!message) return;

    message.hidden = true;
  }
}

class NewsletterDrawerForm extends KlaviyoSubscribeForm {}

if (!customElements.get('klaviyo-subscribe-form')) {
  customElements.define('klaviyo-subscribe-form', KlaviyoSubscribeForm);
}

if (!customElements.get('newsletter-drawer-form')) {
  customElements.define('newsletter-drawer-form', NewsletterDrawerForm);
}
